import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CpuChipIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { monitoringService } from '../services/monitoring';
import { useWorkspace } from '../contexts/WorkspaceContext';

const Monitoring: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { currentWorkspace } = useWorkspace();

  // Fetch real monitoring data
  const { data: systemHealth } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => monitoringService.getSystemHealth(),
    refetchInterval: autoRefresh ? 30000 : false, // Auto-refresh every 30 seconds
  });

  const { data: workspaceMetrics } = useQuery({
    queryKey: ['workspace-metrics', currentWorkspace?.id],
    queryFn: () => monitoringService.getWorkspaceMetrics(currentWorkspace?.id!),
    enabled: !!currentWorkspace?.id,
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const { data: executionTrends } = useQuery({
    queryKey: ['execution-trends', currentWorkspace?.id],
    queryFn: () => monitoringService.getExecutionTrends(currentWorkspace?.id!, 7),
    enabled: !!currentWorkspace?.id,
    refetchInterval: autoRefresh ? 60000 : false, // Refresh every minute
  });

  const { data: recentExecutions } = useQuery({
    queryKey: ['recent-executions', currentWorkspace?.id],
    queryFn: () => monitoringService.getRecentExecutions(currentWorkspace?.id!, 10),
    enabled: !!currentWorkspace?.id,
    refetchInterval: autoRefresh ? 15000 : false, // Refresh every 15 seconds
  });

  // Fix the empty state component to use refresh instead of setHasData
  const EmptyStateGraphic: React.FC<{
    title: string;
    description: string;
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    color: string;
  }> = ({ title, description, Icon, color }) => (
    <div className="h-64 flex flex-col items-center justify-center text-center">
      <div className={`p-4 rounded-full ${color} mb-4`}>
        <Icon className="h-8 w-8 text-white" />
      </div>
      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{title}</h4>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{description}</p>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <ArrowPathIcon className="h-4 w-4 mr-2" />
        Refresh Data
      </button>
    </div>
  );

  // Process execution trends for charts
  const responseTimeData = executionTrends?.map(trend => ({
    name: new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: trend.avg_execution_time
  })) || [];

  const throughputData = executionTrends?.map(trend => ({
    name: new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: trend.total_executions
  })) || [];

  const errorRateData = executionTrends?.map(trend => ({
    name: new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: trend.total_executions > 0 ? (trend.failed_executions / trend.total_executions) * 100 : 0
  })) || [];

  // System metrics
  const systemMetrics = [
    {
      name: 'Total Agents',
      value: '24',
      unit: 'active',
      trend: 12,
      color: 'bg-gradient-to-r from-blue-500 to-cyan-600',
      icon: CpuChipIcon
    },
    {
      name: 'Response Time',
      value: '234',
      unit: 'ms',
      trend: -8,
      color: 'bg-gradient-to-r from-green-500 to-emerald-600',
      icon: ClockIcon
    },
    {
      name: 'Success Rate',
      value: '98.5',
      unit: '%',
      trend: 2,
      color: 'bg-gradient-to-r from-purple-500 to-pink-600',
      icon: CheckCircleIcon
    },
    {
      name: 'Cost Today',
      value: '$12.45',
      unit: 'spent',
      trend: 15,
      color: 'bg-gradient-to-r from-amber-500 to-orange-600',
      icon: CurrencyDollarIcon
    }
  ];

  // Agent performance data with required properties
  const agentPerformanceData = [
    {
      id: '1',
      name: 'Code Analyzer',
      type: 'Analysis',
      status: 'active' as const,
      executions: 1247,
      successRate: 0.985,
      avgResponseTime: 234,
      cost: 2.45
    },
    {
      id: '2',
      name: 'Documentation Agent',
      type: 'Generation',
      status: 'active' as const,
      executions: 892,
      successRate: 0.923,
      avgResponseTime: 189,
      cost: 1.87
    },
    {
      id: '3',
      name: 'Security Scanner',
      type: 'Security',
      status: 'idle' as const,
      executions: 456,
      successRate: 0.967,
      avgResponseTime: 312,
      cost: 0.98
    }
  ];

  // Fetch data using React Query
  const { data: agents } = useQuery({
    queryKey: ['agents', currentWorkspace?.id],
    queryFn: () => monitoringService.getAgents(currentWorkspace?.id!),
    enabled: !!currentWorkspace?.id,
    select: (data) => data?.agents || agentPerformanceData
  });

  const metricCards = [
    {
      title: 'Total Executions',
      value: workspaceMetrics?.total_executions?.toLocaleString() || '0',
      change: '+12%',
      trend: 'up' as const,
      icon: CpuChipIcon,
      color: 'blue' as const
    },
    {
      title: 'Success Rate',
      value: workspaceMetrics?.success_rate ? `${workspaceMetrics.success_rate}%` : '0%',
      change: '+5%',
      trend: 'up' as const,
      icon: CheckCircleIcon,
      color: 'green' as const
    },
    {
      title: 'Avg Response Time',
      value: workspaceMetrics?.avg_execution_time ? `${workspaceMetrics.avg_execution_time.toFixed(1)}s` : '0s',
      change: '-8%',
      trend: 'down' as const,
      icon: ClockIcon,
      color: 'purple' as const
    },
    {
      title: 'Total Tokens',
      value: workspaceMetrics?.total_tokens_used?.toLocaleString() || '0',
      change: '+15%',
      trend: 'up' as const,
      icon: CurrencyDollarIcon,
      color: 'yellow' as const
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case 'increase':
        return <ArrowUpIcon className="h-4 w-4 text-green-500" />;
      case 'decrease':
        return <ArrowDownIcon className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  const timeRanges = [
    { id: '1h', label: '1 Hour' },
    { id: '24h', label: '24 Hours' },
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">System Monitoring</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Real-time insights into your AI agent performance and system health
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Live Updates</span>
            </div>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                autoRefresh
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>

        {/* System Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {systemMetrics.map((metric, index) => (
            <div
              key={metric.name}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.name}</p>
                  <div className="flex items-baseline space-x-2 mt-2">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {metric.value}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {metric.unit}
                    </p>
                  </div>
                  <div className="flex items-center mt-2">
                    {metric.trend > 0 ? (
                      <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                    )}
                    <span className={`text-sm font-medium ${
                      metric.trend > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {Math.abs(metric.trend)}%
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last hour</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${metric.color}`}>
                  <metric.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Response Time Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Response Time</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Average (ms)</span>
              </div>
            </div>
            {responseTimeData.length > 0 ? (
              <div className="h-64 flex items-end justify-between space-x-2">
                {responseTimeData.map((point, index) => (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div 
                      className="w-full bg-blue-500 rounded-t-sm transition-all duration-300 hover:bg-blue-600"
                      style={{ height: `${(point.value / Math.max(...responseTimeData.map(p => p.value))) * 100}%` }}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 transform -rotate-45 origin-top-left">
                      {point.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyStateGraphic 
                title="No Response Time Data"
                description="Start executing workflows to see response time metrics"
                Icon={ClockIcon}
                color="bg-blue-500"
              />
            )}
          </div>

          {/* Throughput Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Throughput</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Requests/min</span>
              </div>
            </div>
            {throughputData.length > 0 ? (
              <div className="h-64 flex items-end justify-between space-x-2">
                {throughputData.map((point, index) => (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div 
                      className="w-full bg-green-500 rounded-t-sm transition-all duration-300 hover:bg-green-600"
                      style={{ height: `${(point.value / Math.max(...throughputData.map(p => p.value))) * 100}%` }}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 transform -rotate-45 origin-top-left">
                      {point.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyStateGraphic 
                title="No Throughput Data"
                description="Execute workflows to see throughput metrics and performance trends"
                Icon={ChartBarIcon}
                color="bg-green-500"
              />
            )}
          </div>
        </div>

        {/* Agent Performance Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-200">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Performance</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Detailed performance metrics for each agent
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Executions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Avg Response
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {agentPerformanceData.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                            <CpuChipIcon className="h-4 w-4 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {agent.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {agent.type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        agent.status === 'active'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : agent.status === 'idle'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          agent.status === 'active' ? 'bg-green-500' :
                          agent.status === 'idle' ? 'bg-yellow-500' : 'bg-red-500'
                        }`} />
                        {agent.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {agent.executions.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-900 dark:text-white font-medium">
                              {(agent.successRate * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="mt-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${
                                agent.successRate >= 0.9 ? 'bg-green-500' :
                                agent.successRate >= 0.7 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${agent.successRate * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {agent.avgResponseTime}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      ${agent.cost.toFixed(4)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                          <ChartBarIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Monitoring; 